import { createSelector, DuckSelector, objectDuckCreator } from '@atc/modular-redux';
import _get from 'lodash/get';

export const getMMTLabel = (name = '') => name.replace(/\s/gi, '-').toLowerCase();
export const getSeoStyle = (name = '') => `/${name.replace(/\s\/\s/gi, '+~+')}`;
export const getSeoMMT = (name = '') => `/${name.replace(/\s/gi, '+')}`;

export default function referenceDuckCreator({
    namespace = '',
    store = 'reference',
    initialState = {},
    ...rest
} = {}) {
    return objectDuckCreator({ namespace, store, initialState, ...rest }).extend({

        selectors: (duck) => ({
            getState: new DuckSelector((selectors) => createSelector(
                selectors.getDuckState,
            )),
            getLocalState: new DuckSelector((selectors) => createSelector(
                selectors.getDuckState,
            )),
            getMakes: new DuckSelector((selectors) => createSelector(
                selectors.getDuckState,
                (localState) => _get(localState, 'makes', {}),
            )),
            getStyles: new DuckSelector((selectors) => createSelector(
                selectors.getDuckState,
                (localState) => _get(localState, 'styles', {}),
            )),
            getMakesByLabel: new DuckSelector((selectors) => createSelector(
                selectors.getMakes,
                (makes) => Object.keys(makes).reduce((acc, makeKey) => {
                    const { label, models, series } = makes[makeKey];
                    const convertedMakeLabel = getMMTLabel(label);
                    acc[convertedMakeLabel] = { ...makes[makeKey] };
                    if (models) {
                        acc[convertedMakeLabel].models = Object.keys(models).reduce((accModels, modelKey) => {
                            const { label: modelName, trims } = models[modelKey];
                            const convertedModelLabel = getMMTLabel(modelName);
                            accModels[convertedModelLabel] = { ...models[modelKey] };
                            if (trims) {
                                accModels[convertedModelLabel].trims = Object.keys(trims).reduce((accTrims, trimKey) => {
                                    accTrims[getMMTLabel(trims[trimKey].label)] = { ...trims[trimKey] };
                                    return accTrims;
                                }, {});
                            }
                            return accModels;
                        }, {});
                    }
                    if (series) {
                        acc[convertedMakeLabel].series = Object.keys(series).reduce((accSeries, seriesKey) => {
                            const { label: seriesName } = series[seriesKey];
                            accSeries[getMMTLabel(seriesName)] = { ...series[seriesKey] };
                            return accSeries;
                        }, {});
                    }
                    return acc;
                }, {}),
            )),
            getMakeSeoUrls: new DuckSelector((selectors) => createSelector(
                selectors.getMakes,
                (makes) => Object.keys(makes).reduce((acc, makeKey) => {
                    const { label, models, series } = makes[makeKey];
                    const makeSeo = getSeoMMT(label);
                    acc[makeKey] = { seoUrl: makeSeo };
                    if (models) {
                        acc[makeKey].models = Object.keys(models).reduce((accModels, modelKey) => {
                            const { label: modelName, trims } = models[modelKey];
                            const modelSeo = makeSeo + getSeoMMT(modelName);
                            accModels[modelKey] = { seoUrl: modelSeo };
                            if (trims) {
                                accModels[modelKey].trims = Object.keys(trims).reduce((accTrims, trimKey) => {
                                    accTrims[trimKey] = { seoUrl: modelSeo + getSeoMMT(trims[trimKey].label) };
                                    return accTrims;
                                }, {});
                            }
                            return accModels;
                        }, {});
                    }
                    if (series) {
                        acc[makeKey].series = Object.keys(series).reduce((accSeries, seriesKey) => {
                            accSeries[seriesKey] = { seoUrl: makeSeo + getSeoMMT(series[seriesKey].label) };
                            return accSeries;
                        }, {});
                    }
                    return acc;
                }, {}),
            )),
            getModelsByYear: new DuckSelector((selectors) => createSelector(
                selectors.getMakes,
                (makes) => Object.keys(makes).reduce((acc, makeKey) => {
                    const { models } = makes[makeKey];
                    if (models) {
                        acc[makeKey] = Object.keys(models).reduce((accModels, modelKey) => {
                            const { years, specsByYear = {}, priceLabelByYear = {}, images = {} } = models[modelKey];
                            if (years) {
                                years.forEach((year) => {
                                    accModels[year] = {
                                        ...accModels[year],
                                        [modelKey]: {
                                            ...models[modelKey],
                                            specs: specsByYear[year],
                                            priceLabel: priceLabelByYear[year],
                                            image: images[year],
                                        },
                                    };
                                });
                            }
                            return accModels;
                        }, {});
                    }
                    return acc;
                }, {}),
            )),
            getStyleSeoUrls: new DuckSelector((selectors) => createSelector(
                selectors.getStyles,
                (styles) => Object.keys(styles).reduce((acc, styleKey) => {
                    acc[styleKey] = { seoUrl: getSeoStyle(styles[styleKey].label) };
                    return acc;
                }, {}),
            )),
        }),
    });
}
